/**
 * @fileoverview ErrorBoundary component for React error handling
 * 
 * @purpose Provides error boundary functionality to catch and display React errors gracefully.
 *          Includes error tracking integration (optional).
 * 
 * @usage Import in app layout or page components:
 *        import { ErrorBoundary } from '@korvo/core/components/shared'
 * 
 * @ownership Core infrastructure - error handling
 */

'use client'

import React from 'react'

type ErrorBoundaryState = {
  hasError: boolean
  error?: Error
  errorInfo?: React.ErrorInfo
}

type ErrorBoundaryProps = {
  children: React.ReactNode
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>
  errorTracker?: {
    trackError: (error: Error, metadata?: Record<string, unknown>, severity?: string) => void
  }
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo
    })

    // Track the error if errorTracker is provided
    if (this.props.errorTracker) {
      this.props.errorTracker.trackError(error, {
        component: 'ErrorBoundary',
        stack: error.stack,
        metadata: {
          componentStack: errorInfo.componentStack
        }
      }, 'high')
    }
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback
        return <FallbackComponent error={this.state.error!} resetError={this.resetError} />
      }

      return <DefaultErrorFallback error={this.state.error!} resetError={this.resetError} />
    }

    return this.props.children
  }
}

function DefaultErrorFallback({ error, resetError }: { error: Error; resetError: () => void }) {
  const errorId = (error as Error & { digest?: string; code?: string; errorId?: string }).digest
    || (error as Error & { code?: string }).code
    || (error as Error & { errorId?: string }).errorId
    || undefined;
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-gray-900">Something went wrong</h3>
          </div>
        </div>
        
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            An unexpected error occurred. Our team has been notified and is working to fix the issue.
          </p>
          {errorId && (
            <p className="mt-2 text-xs text-gray-500 font-mono">
              Error ID: {errorId} — see docs/ERROR_CODES.md for details.
            </p>
          )}
        </div>

        {(process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') && (
          <details className="mb-4">
            <summary className="text-sm font-medium text-gray-700 cursor-pointer">
              Error Details ({process.env.NODE_ENV === 'test' ? 'Test' : 'Development'})
            </summary>
            <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono text-gray-800 overflow-auto max-h-32">
              <div className="font-semibold text-red-600">{error.name}: {(error instanceof Error ? (error instanceof Error ? error.message : String(error)) : String(error))}</div>
              <pre className="mt-2 whitespace-pre-wrap">{error.stack}</pre>
            </div>
          </details>
        )}

        <div className="flex space-x-3">
          <button
            onClick={resetError}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.href = '/'}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Go Home
          </button>
        </div>
      </div>
    </div>
  )
}
